import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { useAuth } from "../../hooks/useAuth";
import {
  Briefcase,
  Menu, Search,
  Users,
  Sparkles,
  Shield,
  Heart,
  HelpCircle,
  RotateCcw,
  X,
} from "lucide-react";

const navLinks = [
  { path: "/candidate", label: "For Candidates", icon: Users },
  { path: "/employer", label: "For Employers", icon: Briefcase },
  { path: "/returner-hub", label: "Returner Hub", icon: RotateCcw },
  { path: "/life", label: "Life Platform", icon: Heart },
  { path: "/help", label: "Help Centre", icon: HelpCircle },
];

const hiddenRoutes = [
  "/employer/dashboard",
  "/employer/settings",
  "/candidate/dashboard",
  "/candidate/profile",
  "/candidate/profile-strength",
  "/candidate/profile-section",
  "/candidate/settings",
  "/admin",
  "/subscription",
];

export function SiteNavigation() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const openCommandPalette = () => window.dispatchEvent(new Event("open-command-palette"));

  const roleClaim = (user as { [key: string]: unknown } | undefined)?.["https://lighthouse.jobs/roles"];
  const roles = Array.isArray(roleClaim) ? roleClaim.filter((entry): entry is string => typeof entry === "string") : [];

  const hasAdminRole = roles.includes("admin");
  const hasEmployerRole = roles.includes("employer");
  const hasCandidateRole = roles.includes("candidate");

  const dashboardLink = !isAuthenticated
    ? "/employer"
    : hasAdminRole
      ? "/admin"
      : hasEmployerRole
        ? "/employer/dashboard"
        : hasCandidateRole
          ? "/candidate/dashboard"
          : "/employer";

  const dashboardLabel = !isAuthenticated
    ? "Employer Login"
    : hasAdminRole
      ? "Admin Dashboard"
      : hasEmployerRole
        ? "Employer Dashboard"
        : hasCandidateRole
          ? "Candidate Dashboard"
          : "Employer Login";

  const DashboardIcon = hasAdminRole ? Shield : hasCandidateRole ? Users : Sparkles;

  if (hiddenRoutes.some((path) => location.pathname.startsWith(path))) {
    return null;
  }

  return (
    <header className="fixed left-0 right-0 top-0 z-50 border-b border-white/10 bg-[#101225]/80 backdrop-blur-md">
      <nav aria-label="Primary" className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-400 via-indigo-500 to-sky-500">
            <Briefcase className="h-5 w-5 text-[#0b1428]" aria-hidden />
          </span>
          <span className="hidden text-sm font-semibold uppercase tracking-[0.2em] text-indigo-100 sm:inline">
            Lighthouse Jobs
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map(({ path, label }) => (
            <NavLink
              key={path}
              to={path}
              className={({ isActive }) =>
                [
                  "rounded-lg px-4 py-2 text-sm font-medium transition",
                  isActive
                    ? "bg-white/10 text-indigo-200 shadow-inner shadow-indigo-500/20"
                    : "text-indigo-100 hover:bg-white/5 hover:text-indigo-200",
                ].join(" ")
              }
            >
              {label}
            </NavLink>
          ))}
          <Button
            variant="ghost"
            className="ml-2 flex items-center gap-2 text-sm text-indigo-100 hover:bg-white/10"
            onClick={openCommandPalette}
          >
            <Search className="h-4 w-4" aria-hidden />
            Quick search
            <kbd className="rounded border border-white/20 px-2 py-0.5 text-[10px] text-white/80">⌘K</kbd>
          </Button>
          <Button asChild className="ml-2 bg-gradient-to-r from-indigo-400 via-indigo-500 to-sky-500 text-slate-900 hover:from-indigo-300">
            <Link to={dashboardLink}>
              <DashboardIcon className="mr-2 h-4 w-4" aria-hidden />
              {dashboardLabel}
            </Link>
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" className="text-white">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="border-white/10 bg-[#050a18] text-white sm:max-w-sm">
            <div className="mt-10 space-y-6">
              {navLinks.map(({ path, label, icon: Icon }) => (
                <NavLink
                  key={path}
                  to={path}
                  className={({ isActive }) =>
                    [
                      "flex items-center gap-3 rounded-xl px-4 py-3 text-lg font-medium transition",
                      isActive
                        ? "bg-white/10 text-indigo-200 shadow-inner shadow-indigo-500/10"
                        : "hover:bg-white/5 hover:text-indigo-200",
                    ].join(" ")
                  }
                  onClick={() => setOpen(false)}
                >
                  <Icon className="h-5 w-5" aria-hidden />
                  {label}
                </NavLink>
              ))}
              <Button asChild className="w-full bg-gradient-to-r from-indigo-400 via-indigo-500 to-sky-500 text-slate-900" size="lg">
                <Link to={dashboardLink} onClick={() => setOpen(false)}>
                  <DashboardIcon className="mr-2 h-5 w-5" aria-hidden />
                  {dashboardLabel}
                </Link>
              </Button>
              <Button
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/5"
                onClick={() => {
                  setOpen(false);
                  openCommandPalette();
                }}
              >
                <Search className="mr-2 h-4 w-4" aria-hidden />
                Command palette
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
}
